import { useEffect, useState } from 'react';

import { getPypeConnections } from '../utils/get-pype-connections';
import { useProStudioCtxSelector } from '../xstate/app.xstate';

// Answers already fetched this session, by project.
const known = new Map<string, boolean>();

/**
 * Whether the project has any live agent platform connected: `null` until
 * known. Uses the list the Live Agent Platforms page loaded when there is
 * one, and otherwise reads it directly (the same query), so Design never
 * sends the machine into a loading state just to ask.
 */
export const useHasLiveAgentPlatform = (): boolean | null => {
  const projectId = useProStudioCtxSelector((ctx) => ctx.project?.id);
  const loaded = useProStudioCtxSelector((ctx) => ctx.pypeConnections);
  const [fetched, setFetched] = useState<boolean | null>(() =>
    projectId && known.has(projectId) ? known.get(projectId)! : null
  );

  useEffect(() => {
    if (!projectId || loaded || known.has(projectId)) return undefined;
    let live = true;
    getPypeConnections({ projectId })
      .then((connections) => {
        known.set(projectId, connections.length > 0);
        if (live) setFetched(connections.length > 0);
      })
      .catch(() => {
        // Unknown stays unknown: Design then says nothing rather than guess.
      });
    return () => {
      live = false;
    };
  }, [projectId, loaded]);

  if (loaded) return loaded.length > 0;
  if (projectId && known.has(projectId)) return known.get(projectId)!;
  return fetched;
};
